import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { usePageFlow } from '@hooks/usePageFlow'
import { usePathBuilder } from '@hooks/usePathBuilder'
import { useAppNavigate } from '@hooks/useAppNavigate'
import { initClaimStatus } from '@redux/slices/claimStatusSlice'
import { CustomerFacingClaimRecordStatus } from '@typesApp/ClaimRecord'
import { useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { resetLastForm } from '@redux/slices/formsSlice'
import { closeApp, unhandledError } from '@redux/slices/workflowSlice'
import { getCustomerFacingStatus } from '@utils/claimUtil'
import { LoadingSkeleton } from './LoadingSkeleton'

type ResumeState = {
  claimRecordId?: string
}

export const ResumeRedirect: React.FC = () => {
  const buildPath = usePathBuilder()
  const navigate = useAppNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { defaultView, getResumeToView } = usePageFlow()
  const [fetchClaimRecord, isLoading] = useLazyGetClaimRecordQuery()

  const state: ResumeState = location.state

  useEffect(() => {
    if (!state?.claimRecordId) return

    fetchClaimRecord(state.claimRecordId)
      .unwrap()
      .then(claimRecord => {
        dispatch(resetLastForm())
        dispatch(initClaimStatus(claimRecord))

        // resume to view configured in pageflow
        const status = getCustomerFacingStatus(claimRecord)

        if (status === CustomerFacingClaimRecordStatus.IN_PROGRESS) {
          const resumeToView = getResumeToView(claimRecord)
          if (resumeToView) {
            navigate(buildPath(resumeToView))
            return
          }
        }

        // otherwise navigate to default view
        navigate(buildPath(defaultView))
      })
      .catch(error => {
        dispatch(closeApp())

        // TODO: add listener for unhandled error to show generic error modal
        dispatch(unhandledError(error))
      })
  }, [dispatch, buildPath, navigate, fetchClaimRecord, getResumeToView, state, defaultView])

  return isLoading ? <LoadingSkeleton /> : null
}
